import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  ExcelExportParams,
  GridApi,
  GridOptions,
} from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { cloneDeep } from 'lodash-es';
import { combineLatest, firstValueFrom, switchMap } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { InvoiceWorkflowDirective } from '@features/feature-workflow/directives/invoice-workflow.directive';

import { ButtonComponent } from '@shared/components/button/button.component';
import { ErrorsTooltipComponent } from '@shared/components/errors-tooltip/errors-tooltip.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  ActivityType,
  Currency,
  EntityType,
  EventType,
  getInvoiceDetailsGridQuery,
  GqlService,
  PermissionType,
  UpdateInvoiceDetailInput,
  WorkflowStep,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventQuery } from '@shared/store/event/event.query';
import { EventService } from '@shared/store/event/event.service';
import { InvoiceModel } from '@shared/store/invoice/invoice.model';
import { MainQuery } from '@shared/store/main/main.query';
import { MainStore } from '@shared/store/main/main.store';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { AuxExcelStyles } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import {
  calculatePinnedBottomData,
  parseColumnDefs,
  refactorToNumbers,
} from './invoice-items-via-pdf.constants';
import { InvoiceItemColumnType } from './invoice-items-via-pdf.model';
import { invoiceDisabledTooltip } from '../../../invoices.component';
import { InvoiceFormUtils } from '../../invoice-form.utils';
import { InvoiceItemsIntegrationComponent } from '../invoice-items-integration/invoice-items-integration.component';
import { InvoiceItemsNoRowsComponent } from '../invoice-items-no-rows.component';
import { MapActivitiesModalService } from '../map-activities/map-activities-modal/map-activities-modal.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [InvoiceWorkflowDirective],
  imports: [
    AgGridAngular,
    ButtonComponent,
    TooltipDirective,
    InvoiceItemsIntegrationComponent,
    InvoiceItemsNoRowsComponent,
    StickyGridDirective,
    ErrorsTooltipComponent,
  ],
  selector: 'aux-invoice-items-via-pdf',
  templateUrl: 'invoice-items-via-pdf.component.html',
})
export class InvoiceItemsViaPdfComponent implements OnInit {
  authQuery = inject(AuthQuery);
  authService = inject(AuthService);
  eventQuery = inject(EventQuery);
  eventService = inject(EventService);
  gqlService = inject(GqlService);
  invoiceCtx = inject(InvoiceWorkflowDirective);
  mainQuery = inject(MainQuery);
  mainStore = inject(MainStore);
  overlayService = inject(OverlayService);
  workflowQuery = inject(WorkflowQuery);

  private readonly destroyRef = inject(DestroyRef);
  private mapActivitiesModalService = inject(MapActivitiesModalService);

  colDefs = signal<ColDef[]>([]);
  editMode = signal(false);
  gridAPI = signal<GridApi | null>(null);
  gridChangeList = signal<UpdateInvoiceDetailInput[]>([]);
  gridData = signal<
    Record<
      string,
      {
        value: number | string;
      }
    >[]
  >([]);
  gridDataLoading = signal(true);

  eventProcessing = computed(() => {
    return this.eventQuery.selectProcessingEvent(EventType.INVOICE_DOCUMENT_UPLOADED)();
  });

  invoiceLockTooltip = this.invoiceCtx.invoiceLockTooltip;

  getEditBtnTooltip = computed(() => {
    const isAdmin = this.authQuery.adminUser();

    if (!isAdmin && !this.hasEditPermission()) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    if (this.statusCheck()) {
      return invoiceDisabledTooltip;
    }

    return this.invoiceLockTooltip();
  });
  gridOptions = computed(() => {
    const columnDefs = this.colDefs();

    return untracked(
      () =>
        <GridOptions>{
          defaultColDef: {
            ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
          },
          ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
          columnDefs: columnDefs.map((def) => {
            return {
              ...def,
              editable: (cell: EditableCallbackParams) => {
                if (
                  cell.node.group ||
                  (cell.node.data.activity_type === ActivityType.ACTIVITY_DISCOUNT &&
                    cell.colDef.field !== 'vendor_estimate_amount' &&
                    cell.colDef.field !== 'vendor_estimate_amount_ltd') ||
                  cell.node?.isRowPinned()
                ) {
                  return false;
                }

                return this.editMode();
              },
              flex: 1,
            };
          }),
          enableRangeSelection: true,
          excelStyles: [
            ...AuxExcelStyles,
            ...Utils.generateExcelCurrencyStyles(Utils.CURRENCY_OPTIONS),
          ],
          suppressCellFocus: false,
        }
    );
  });
  hasChanges = computed(() => {
    return this.gridChangeList().length > 0;
  });

  invoice = input.required<InvoiceModel>();

  statusCheck = computed(() => {
    const trial = this.mainQuery.selectedTrial();
    const { currentOpenMonth } = this.mainQuery.mainState();
    const invoice = this.invoice();

    return InvoiceFormUtils.isAccrualPeriodInClosedMonth({ currentOpenMonth, invoice, trial });
  });

  hasEditPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_UPDATE_INVOICES],
  });

  isEditBtnDisabled = computed(() => {
    const isAdmin = this.authQuery.adminUser();

    return (
      (!this.hasEditPermission() && !isAdmin) ||
      !!this.invoiceLockTooltip() ||
      this.statusCheck() ||
      this.gridData().length < 1
    );
  });
  isOldInvoice = computed(() => {
    const invoice = this.invoice();
    const gridData = this.gridData();
    const items = invoice.ocr_line_items || [];

    return gridData.length < 1 && items.length > 0;
  });
  loading = computed(() => this.eventProcessing() || this.gridDataLoading());
  mapToBudgetTooltip = computed(() => {
    const errors: string[] = [];

    if (!this.hasEditPermission()) {
      errors.push(MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION);
    }

    if (this.isInvoiceFinalized()) {
      errors.push(this.invoiceLockTooltip());
    }

    if (this.editMode()) {
      errors.push(MessagesConstants.INVOICE.LINE_ITEMS_IN_EDIT_MODE);
    }

    if (!this.gridData().length) {
      errors.push(MessagesConstants.INVOICE.NO_LINE_ITEMS_TO_MAP);
    }

    return errors;
  });

  @Output() changeTab = new EventEmitter<'pdf'>();
  editModeEffect = effect(() => {
    const gridAPI = this.gridAPI();
    const editMode = this.editMode();
    const colDefs = this.colDefs();

    if (!gridAPI) {
      return;
    }

    if (editMode) {
      gridAPI.startEditingCell({
        colKey: colDefs[0].field || '',
        rowIndex: 0,
      });
    }

    gridAPI.refreshCells({ force: true });
  });
  isInvoiceFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_INVOICES
  );

  private columnsWithNumbers: string[] = [];

  setBottomData = effect(() => {
    const gridAPI = this.gridAPI();
    const gridData = this.gridData();

    this.gridChangeList();

    if (!gridAPI) {
      return;
    }

    untracked(() => {
      const col = this.colDefs()[0].field?.replace('.value', '') || '';
      const pinnedBottomData = calculatePinnedBottomData(gridData, this.columnsWithNumbers);

      gridAPI.setGridOption('pinnedBottomRowData', [
        {
          [col]: {
            value: 'Total',
          },
          ...pinnedBottomData,
        },
      ]);
    });
  });

  private initialGridData: { [p: string]: { value: number | string } }[] = [];

  ngOnInit(): void {
    this.initGrid();
  }

  autosize() {
    this.gridAPI()?.sizeColumnsToFit();
  }

  canDeactivate() {
    return !this.hasChanges();
  }

  constructor() {
    combineLatest([
      toObservable(this.invoice),
      this.eventQuery.selectProcessingEvent$(EventType.INVOICE_DOCUMENT_UPLOADED),
    ])
      .pipe(
        filter(([, eventProcessing]) => eventProcessing === false),
        tap(() => this.gridDataLoading.set(true)),
        switchMap(([invoice]) =>
          this.gqlService.getInvoiceDetailsGrid$(EntityType.INVOICE_LINE, invoice.id)
        ),
        takeUntilDestroyed()
      )
      .subscribe((resp) => this.parseInvoiceGridResponse(resp));
  }

  getDynamicExcelParams() {
    const trial = this.mainQuery.getSelectedTrial();
    const gridAPI = this.gridAPI();
    const invoice = this.invoice();

    if (!trial || !gridAPI) {
      return;
    }

    const totals = gridAPI.getPinnedBottomRow(0)?.data || {};

    const appendContent: ExcelExportParams['appendContent'] = [
      {
        cells: [
          {
            data: {
              type: 'String',
              value: 'Total',
            },
            styleId: ['total_row_header'],
          },
        ],
      },
    ];

    gridAPI
      .getAllDisplayedColumns()
      .filter((key) => key.getColId() !== 'col_1.value')
      .forEach((col) => {
        if (
          col.getColDef().type === InvoiceItemColumnType.UNIT_PRICE ||
          col.getColDef().type === InvoiceItemColumnType.PRICE
        ) {
          appendContent[0].cells.push({
            data: {
              type: 'Number',
              value: `${totals[col.getColId().replace('.value', '')].value}`,
            },
            styleId: [`total_row_${invoice.organization.currency || Currency.USD}`],
          });
        } else {
          appendContent[0].cells.push({
            data: { type: 'String', value: '' },
            styleId: ['total_row'],
          });
        }
      });

    const exportOptions: ExcelExportParams = {
      allColumns: true,
      appendContent: appendContent,
      author: 'Auxilius',
      fileName: `${invoice.invoice_no}_${invoice.organization.name}_${trial.short_name}_Auxilius_Parsed_Invoice_Line_Items.xlsx`,
      fontSize: 11,
      prependContent: [
        {
          cells: [
            {
              data: { type: 'String', value: `Trial: ${trial.short_name}` },
              mergeAcross: appendContent[0].cells.length - 1,
              styleId: 'first_row',
            },
          ],
        },
      ],
      sheetName: 'Invoice Line Items',
      skipPinnedBottom: true,
    };

    gridAPI.exportDataAsExcel(exportOptions);
  }

  private initGrid(): void {
    this.gqlService
      .getInvoiceDetailsGrid$(EntityType.INVOICE_LINE, this.invoice().id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.gridDataLoading.set(true))
      )
      .subscribe((resp) => this.parseInvoiceGridResponse(resp));
  }

  onCancel() {
    this.editMode.set(false);
    this.gridChangeList.set([]);
    this.gridData.set(cloneDeep(this.initialGridData));
  }

  onCellValueChanged(event: CellValueChangedEvent): void {
    const colId = event.colDef.field?.replace('.value', '') || '';
    const cellId = event.data[colId].id;
    const index = this.gridChangeList().findIndex((item) => item.id === cellId);

    if (index === -1) {
      this.gridChangeList.set([
        ...this.gridChangeList(),
        {
          entity_field_source: 'MANUAL',
          entity_field_value: event.newValue || '',
          id: cellId,
        },
      ]);
    } else {
      this.gridChangeList.set(
        this.gridChangeList().map((item) => {
          if (item.id === cellId) {
            item.entity_field_value = event.newValue || '';
          }

          return item;
        })
      );
    }

    // if (
    //   event.colDef.type === InvoiceItemColumnType.UNIT_PRICE ||
    //   event.colDef.type === InvoiceItemColumnType.PRICE
    // ) {
    //   this.setPinnedBottomRowData();
    // }
  }

  onSave = async () => {
    this.editMode.set(false);

    const { data, success } = await firstValueFrom(
      this.gqlService.updateInvoiceDetail$(this.gridChangeList())
    );

    if (success && data) {
      this.overlayService.success(MessagesConstants.SUCCESSFULLY_SAVED);
      this.initGrid();
    } else {
      this.gridData.set(cloneDeep(this.initialGridData));
    }

    this.gridChangeList.set([]);
  };

  openMapActivitiesModal(): void {
    this.changeTab.emit('pdf');
  }

  parseInvoiceGridResponse({ data, errors }: GraphqlResponse<getInvoiceDetailsGridQuery>) {
    if (!data) {
      this.overlayService.error(errors);

      return;
    }

    const columnDefs = parseColumnDefs(
      data.column_defs || '[]',
      this.invoice().organization.currency
    );

    this.colDefs.set(columnDefs);

    this.columnsWithNumbers = columnDefs
      .filter(
        (column) =>
          column.type === InvoiceItemColumnType.UNIT_PRICE ||
          column.type === InvoiceItemColumnType.PRICE
      )
      .map((col) => col.field.replace('.value', ''));

    const gridData = (
      JSON.parse(data.row_data || '[]') as Record<
        string,
        {
          value: number | string;
        }
      >[]
    ).map((row) => {
      const obj: Record<
        string,
        {
          value: number | string;
        }
      > = {};

      try {
        this.columnsWithNumbers.forEach((column) => {
          const value = row[column]?.value;

          if (value) {
            obj[column] = {
              ...row[column],
              value: refactorToNumbers(value as string),
            };
          }
        });
      } catch (error) {
        console.error(error);
      }

      return {
        ...row,
        ...obj,
      };
    });

    let firstPriceColumn: (ColDef & { field: string }) | undefined;

    firstPriceColumn = columnDefs.find((column) => column.type === InvoiceItemColumnType.PRICE);

    if (!firstPriceColumn) {
      firstPriceColumn = columnDefs.find(
        (column) =>
          column.headerName === 'Amount' ||
          column.headerName === 'Total' ||
          column.headerName === 'Dollar' ||
          column.headerName === 'Actual Amount'
      );
    }

    this.mapActivitiesModalService.setLineItemPDFOptions(
      gridData,
      firstPriceColumn?.field.replace('.value', '') || ''
    );
    this.gridData.set(gridData);
    this.initialGridData = cloneDeep(gridData);
    this.gridDataLoading.set(false);
  }
}
